import React, { useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import styled, { cx, StylesProps } from '../../../../lib/styles';

import { Article, Author, Category } from '../../../../models/article';
import Spacer from '../../../../components/presentational/Spacer';
import { Ads } from '../../../../components/presentational/Ad';
import blockStyles from './Block_styles';
import { GetObjectsOptions, SearchOptions } from '@algolia/client-search';
import { algoliaDataToRetrieve, articleAsAlgoliaObject, ArticleItem } from '../../../../lib/algolia/types';
import { useAlgoliaSearch } from '../../../../lib/hooks/useAlgoliaSearch';
import ArticleTileBlock, { ArticleTileColumn } from '../../../../components/presentational/ArticleTileBlock/ArticleTileBlock';
import SkeletonLoader from '../../../../components/presentational/SkeletonLoader/SkeletonLoader';
import { cfImage, truncateStringByWord } from '../../../../lib/stringUtils';
import theme from '../../../../lib/theme';
import { useAlgoliaObjects } from '../../../../lib/hooks/useAlgoliaObjects';
import ArticleReelBlock from '../../../../components/presentational/ArticleReelBlock/ArticleReelBlock';
const { mediaSizes } = theme;

export interface ArticleBlockData {
  type: 'article_block';
  articles: Article[];
  hideAds: boolean;
  hideStickyAd: boolean;
  hideTopAd: boolean;
  title?: string;
  subtitle?: string;
  moreArticlesURL: string;
  hideBottomAd: boolean;
  feature: 'top' | 'heavy';
  teaser: {
    showMoreText: string;
    title: string;
    titleOverride: string;
    article: Article;
  };
  aside: {
    icon: 'bookmark' | 'avatar';
    title: string;
    articles: Article[];
  };
  pagination: boolean;
  backfillCount: number;
  preferTrackingBackfill: boolean;
	onlyShowHeroNoArticles?: boolean;
	showNoCategories?: boolean;
	showNoTitle?: boolean;
}

interface ArticleBlockProps {
  block: ArticleBlockData;
  ads: Ads;
  blockIndex: number;
  categories?: Category[];
  links?: { id: number, label: string, url: string }[];
  categoryDescendents?: { id: number, label: string, url: string }[];
  backfillCategory?: Category;
  author: Author | undefined;
  hideAds: boolean;
  lazy: boolean;
  loadMore: (
    moreArticlesURL: string | undefined,
    i: number,
    articleIds: number[]
  ) => any;
  articleIds: number[];
  moreArticlesLoading: boolean | undefined;
  collectionConfig?: { [key: string]: any; };
}

const ArticleBlock = ({
  block,
  block: {
    articles,
		onlyShowHeroNoArticles,
		showNoCategories,
		showNoTitle,
    // pagination,
    // moreArticlesURL,
    // teaser,
    // aside,
    // title,
    // subtitle,
    // hideStickyAd,
    // hideTopAd,
    // hideBottomAd,
    // backfillCount,
    // preferTrackingBackfill,  
  },
  blockIndex,
  categories, 
  collectionConfig,
  articleIds,
  links,
  categoryDescendents,
  backfillCategory,
  // //@ts-ignore
  // ads,
  // //@ts-ignore
  // hideAds,
  // //@ts-ignore
  // author,
  // //@ts-ignore
  // loadMore,
  // //@ts-ignore
  // moreArticlesLoading,
  // //@ts-ignore
  styles,
}: ArticleBlockProps & StylesProps<typeof blockStyles>) => {
  /**
   * Category collections which have no subcategories will all show the same articles when purely using backfill.
   * TODO: 
   * 1. Populate articles from those selected in the block
   * 2. Populate articles specifically linked to the category being viewed
   * 3. Backfill - articles of parent/sibling categories? Backfill could use session cookie to not repeat articles already viewed.
   *    This would require articles to be stored in session cookie when viewed.
   */
  const { hideAds: hideAdsFromBlock } = block;
  const { title: collectionTitle, subtitle: collectionSubtitle } = collectionConfig || {};
  const ALGOLIA_INDEX_NAME = 'production_media_starlord'; // Define your Algolia index name
  const HERO_COUNT = 5; // number of tiles in the hero row (selected first, then backfill)
  const MAX_ARTICLES = 29; // total tiles the block can render (5 hero + 24 list); also the block's max selectable articles

  const [heroArticles, setHeroArticles] = React.useState<ArticleItem[]>([]);

  //  Backfill strategy (SA60-1336): fill the block from most- to least-relevant, and
  //  widen UP THE TREE so the page is never short when a narrow category is thin.
  //  Precedence (highest first), each deduped against the ones above:
  //    tier 1 — the viewed category as an article's PRIMARY category (+ subcategories)
  //    tier 2 — the viewed category as a SECONDARY tag (+ subcategories)
  //    tier 3 — the full breadcrumb hierarchy (ancestors) to fill any remaining slots
  //
  //  Three plain `filters` queries merged client-side, NOT Algolia optionalFilters:
  //  on this index only `primaryCategory.id` is a scoring facet (`categories.id` is
  //  not), so optionalFilters cannot rank secondary-tag matches. Splitting primary vs
  //  secondary into their own queries is also what lets primary-category articles lead
  //  even when they are older than more recent secondary-tagged ones.
  //
  //  `backfillCategory` (the viewed term) is sent explicitly by WordPress; when it is
  //  absent we fall back to the most specific entry of `categories` (ordered pillar-
  //  first, so the viewed category is last). `categories` is the breadcrumb hierarchy
  //  (viewed category + ancestors) that drives the widening tier.
  const viewedCategory = backfillCategory
    || (categories && categories.length > 0 ? categories[categories.length - 1] : undefined);
  const descendentIds = (categoryDescendents || []).map(category => category.id);
  const specificIds = [viewedCategory?.id, ...descendentIds];
  const treeIds = [...(categories || []).map(category => category.id), ...descendentIds];

  const dedupeFilter = articleIds.length > 0
    ? `(${articleIds.map(id => `NOT objectID:"post#${id}"`).join(' AND ')})`
    : '';
  const buildBackfillFilters = (
    categoryIds: (number | undefined)[],
    match: 'primary' | 'secondary' | 'any'
  ) => {
    const validIds = categoryIds.filter((id): id is number => id != null);
    const clause = (id: number) =>
      match === 'primary' ? `primaryCategory.id:${id}`
        : match === 'secondary' ? `categories.id:${id}`
          : `(primaryCategory.id:${id} OR categories.id:${id})`;
    const categoryFilter = validIds.length > 0
      ? `(${validIds.map(clause).join(' OR ')})`
      : '';
    return [categoryFilter, dedupeFilter].filter(Boolean).join(' AND ');
  };

  const primaryFilters = buildBackfillFilters(specificIds, 'primary');
  const secondaryFilters = buildBackfillFilters(specificIds, 'secondary');
  const treeFilters = buildBackfillFilters(treeIds, 'any');

  const primarySearchOptions = useMemo<SearchOptions>(() => ({
    filters: primaryFilters,
    hitsPerPage: 50,
    attributesToRetrieve: algoliaDataToRetrieve,
  }), [primaryFilters]);
  const secondarySearchOptions = useMemo<SearchOptions>(() => ({
    filters: secondaryFilters,
    hitsPerPage: 50,
    attributesToRetrieve: algoliaDataToRetrieve,
  }), [secondaryFilters]);
  const treeSearchOptions = useMemo<SearchOptions>(() => ({
    filters: treeFilters,
    hitsPerPage: 50,
    attributesToRetrieve: algoliaDataToRetrieve,
  }), [treeFilters]);

  const {
    data: primaryArticles,
    loading: primaryArticlesLoading,
  } = useAlgoliaSearch<ArticleItem>(ALGOLIA_INDEX_NAME, '', primarySearchOptions); // Empty query string as we are using filters
  const {
    data: secondaryArticles,
    loading: secondaryArticlesLoading,
  } = useAlgoliaSearch<ArticleItem>(ALGOLIA_INDEX_NAME, '', secondarySearchOptions);
  const {
    data: treeArticles,
    loading: treeArticlesLoading,
    error: backfillArticlesError,
  } = useAlgoliaSearch<ArticleItem>(ALGOLIA_INDEX_NAME, '', treeSearchOptions);

  //  Merge tiers in precedence order (primary -> secondary -> up the tree), deduped by
  //  objectID so each article appears once, at its highest-precedence position.
  const backfillArticles = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...(primaryArticles || []),
      ...(secondaryArticles || []),
      ...(treeArticles || []),
    ].reduce<ArticleItem[]>((merged, article) => {
      if (article && !seen.has(article.objectID)) {
        seen.add(article.objectID);
        merged.push(article);
      }
      return merged;
    }, []);
  }, [primaryArticles, secondaryArticles, treeArticles]);
  const backfillArticlesLoading =
    primaryArticlesLoading || secondaryArticlesLoading || treeArticlesLoading;

  // const sidebarReelArticleIds = useMemo(() => (['post#760194']), []);
  // const sidebarReelArticlesAlgoliaOptions = useMemo<GetObjectsOptions>(() => ({
  //   attributesToRetrieve: algoliaDataToRetrieve,
  // }), []);

  // const {
  //   data: sidebarReelArticles,
  //   loading: sidebarReelArticlesLoading,
  //   error: sidebarReelArticlesError,
  // } = useAlgoliaObjects<ArticleItem>(sidebarReelArticleIds, ALGOLIA_INDEX_NAME, sidebarReelArticlesAlgoliaOptions);

  //  DEBUGGING
  // useEffect(() => {
  //   console.log('ArticleBlock useEffect', {
  //     heroArticles,
  //     backfillArticles,
  //     categories,
  //     categoryDescendents,
  //     articleIds,
  //     blockIndex,
  //     articles,
  //     styles,
  //   });
  // }, [backfillArticlesLoading]);

  //  Display order: ALL selected (block) articles first, in the editor's order, then
  //  backfill resumes to fill the rest of the page (never short). Backfill already
  //  excludes the selected ids via the dedup filter; we de-dupe again defensively.
  //  Capped at MAX_ARTICLES (5 hero + 24 list). SA60-1336.
  const orderedArticles = useMemo(() => {
    const selected = articles.map(article => articleAsAlgoliaObject({ article }));
    const seen = new Set(selected.map(article => article.objectID));
    const backfill = (backfillArticles || []).filter(
      article => article && !seen.has(article.objectID)
    );
    return [...selected, ...backfill].slice(0, MAX_ARTICLES);
  }, [articles, backfillArticles]);

  useEffect(() => {
    setHeroArticles(orderedArticles.slice(0, HERO_COUNT));
  }, [orderedArticles]);

  //  Everything after the hero row: remaining selected articles first, then backfill.
  const allArticles = useMemo(
    () => orderedArticles.slice(HERO_COUNT),
    [orderedArticles]
  );

  //  Initialise Google Tag Manager for ad slots
  useEffect(() => {
    window.googletag = window.googletag || {cmd: []};
    window.googletag.cmd.push(function() {
      window.googletag.pubads().addEventListener('slotRenderEnded', function(event) {
        const elementsToExclude = ['adhesion'];
        const slotElementId = event.slot.getSlotElementId();
        if (elementsToExclude.some(element => slotElementId.toString().includes(element))) return;

        const adSlotDiv = document.getElementById(slotElementId);
        const parentAdDiv = adSlotDiv!.closest('.ad') as HTMLDivElement;
        if (event.isEmpty) {
          // console.log('An ad slot was empty. Hiding its parent div.', { slotElementId, adSlotDiv, parentAdDiv });
          if (parentAdDiv) parentAdDiv.style.display = 'none';
        } else {
          if (!parentAdDiv) return; // no parent to resize
          const creativeHeight = event.size[1] === 0 ? 180 : event.size[1]; //  cater for fluid sizes ([0,0])
          // console.log('This is an active ad slot.', { creativeHeight, event, slot: event.slot, slotElementId, adSlotDiv, parentAdDiv });
          parentAdDiv.style.minHeight = `${creativeHeight}px`;
        }
      });
    });

    window.googletag.cmd.push(function() {
      // --- AD SLOT DEFINITIONS ---
      // Define size mappings once to reuse
      // Collection units used to be desktop-only: this mapping requested NOTHING below
      // 1024px, so Collection: SAS Sticky took zero smartphone and zero tablet
      // impressions over 30 days despite GAM already having 300x250 / 320x50 registered
      // on the unit. It is the best-earning collection unit on desktop (A$63.78 from
      // 12,627 imps, A$5.05 eCPM), so mobile was pure lost inventory.
      //
      // The sticky lives in the sidebar, which is narrow on mobile — a 300x250 fits, a
      // 300x600 does not belong there, so mobile gets the mrec only.
      var stickySizeMapping = window.googletag.sizeMapping()
        .addSize([1024, 0], [[300, 600], [300, 250]])
        .addSize([768, 0], [[300, 250]])
        .addSize([0, 0], [[300, 250], [320, 50]])
        .build();
      
      var recirculationSizeMapping = window.googletag.sizeMapping()
        .addSize([0, 0], ['fluid'])
        .build();
      
      // 300x250 added alongside 320x50 on mobile: the mrec is a core IAB size with far
      // deeper demand than a 320x50 banner, and both top and bottom sit full-width in the
      // content flow (not in the narrow sidebar), so it fits.
      var topBottomSizeMapping = window.googletag.sizeMapping()
        .addSize([1024, 0], [[728, 90], [970, 250]])
        .addSize([768, 0], [[320, 50], [300, 250], [728, 90]])
        .addSize([0, 0], [[320, 50], [300, 250]])
        .build();

      // Define a slot ONLY if its div was actually rendered.
      //
      // All six divs below live inside conditional JSX branches (hero articles
      // present, a second section existing, hideAdsFromBlock), but these slots used to
      // be defined and displayed unconditionally. Any branch that did not render left
      // an orphan slot: defined, added to the ad request, with no div to render into.
      // On /health that produced collection_sas_sticky FIVE times with only one real
      // div — four wasted ad requests on a single page view.
      //
      // This effect runs after render, so getElementById is the authoritative check.
      var defined: string[] = [];
      var defineIfRendered = function (adUnitPath: string, sizes: any, mapping: any, id: string) {
        if (!document.getElementById(id)) return;
        var slot = window.googletag.defineSlot(adUnitPath, sizes, id);
        if (!slot) return; // already defined (e.g. a re-render) — do not double-define
        slot.defineSizeMapping(mapping).addService(window.googletag.pubads());
        defined.push(id);
      };

      var stickySizes = [[300, 600], [300, 250], [320, 50]];

      defineIfRendered('/21875548389/collection_sas_sticky', stickySizes, stickySizeMapping,
        `gpt-ad-21875548389-collection_sas_sticky-${blockIndex}-section-0`);
      defineIfRendered('/21875548389/collection_sas_sticky', stickySizes, stickySizeMapping,
        `gpt-ad-21875548389-collection_sas_sticky-${blockIndex}-section-1`);

      defineIfRendered('/21875548389/collection_recirculation_native/collection_recirculation_native1', ['fluid'], recirculationSizeMapping,
        `gpt-ad-21875548389-collection_recirculation_native1-${blockIndex}-section-0`);
      defineIfRendered('/21875548389/collection_recirculation_native/collection_recirculation_native1', ['fluid'], recirculationSizeMapping,
        `gpt-ad-21875548389-collection_recirculation_native1-${blockIndex}-section-1`);

      defineIfRendered('/21875548389/collection_sas_top', [[728, 190], [320, 50], [300, 250], [728, 90], [970, 250]], topBottomSizeMapping,
        `gpt-ad-21875548389-collection_sas_top-${blockIndex}-section-0`);

      // NOTE: collection_sas_bottom takes ZERO mobile impressions today because the GAM
      // ad unit has no size smaller than 728x90 registered — the mapping below asks for
      // 320x50 on mobile and GAM has nothing eligible to serve. 320x50 + 300x250 must be
      // added to the ad unit in GAM before this can fill. See docs/AD_STACK.md.
      defineIfRendered('/21875548389/collection_sas_bottom', [[728, 190], [728, 90], [970, 250], [320, 50], [300, 250]], topBottomSizeMapping,
        `gpt-ad-21875548389-collection_sas_bottom-${blockIndex}-section-0`);

      // --- AD SERVICES ---
      // These should only be called once.
      window.googletag.pubads();
      window.googletag.pubads().collapseEmptyDivs(true);
      window.googletag.enableServices();

      window.googletag.cmd.push(function() {
        defined.forEach(function (id) {
          window.googletag.display(id);
        });
      });
    });
  }, [blockIndex]);

  return (
    <Spacer space={'s'} width={mediaSizes.xxl ?? '1200px'} justify={'center'} 
      className={cx(`articleBlock-layout`, styles.container)}>
      {(heroArticles && heroArticles.length > 0 && backfillArticlesLoading === false && backfillArticlesLoading === false) ? (
        <>
          <ArticleTileBlock
            title={{
              label: !showNoTitle ? (onlyShowHeroNoArticles ? block.title : collectionTitle || block.title) : undefined,
            }}
            headingLinks={!showNoCategories ? links : []} // Use section links for the heading
            subCategories={!showNoCategories ? categoryDescendents : []}
            cssClasses="hero-articles mt-1"
            blockConfig={{ showTitleLinks: true, totalTitleLinksToShow: 6 }} // Show title links for this block
            columns={
              [
                {
                  width: '50%',
                  columnConfig: {
                    showColumnNav: false,
                    showTileMeta: false,
                  },
                  tileDirection: 'row',
                  articles: heroArticles
                    // .filter(a => a.image && a.image !== '')
                    .slice(0, 1)
                    .map(article => {
                      return {
                        article: {
                          ...article,
                          image: cfImage(article.image, { width: 800 }),
                          title: truncateStringByWord(article.title, 80),
                        },
                        options: {
                          textPosition: 'bottom',
                          isCoverImage: false, // Assuming these are cover images  
                          tileWidth: '100%',
                        },
                        cssClasses: 'hero-article-col-1-tile',
                      };
                    }) || [],
                  cssClasses: `flex-grow-1 hero-article-col hero-article-col-1`,
                },
                {
                  width: '50%',
                  columnConfig: {
                    showColumnNav: false,
                    showTileMeta: false,
                    stackTiles: {
                      stackOnMobile: true, // Stack on mobile
                    }
                  },
                  tileDirection: 'row',
                  articles: heroArticles
                    // .filter(a => a.image && a.image !== '')
                    .slice(1, 5)
                    .map(article => {
                      return {
                        article: {
                          ...article,
                          image: cfImage(article.image, { width: 400 }),
                          title: truncateStringByWord(article.title, 80),
                        },
                        options: {
                          textPosition: 'bottom',
                          isCoverImage: false, // Assuming these are cover images  
                          tileWidth: '50%',
                        },
                        cssClasses: 'hero-article-col-2-tile',
                      };
                    }) || [],
                  cssClasses: `hero-article-col hero-article-col-2`
                },
              ] as ArticleTileColumn[]
            }
          />
        </>
      ) : <SkeletonLoader loaderType={'FeatureTileBlock'} />}
			
			{!onlyShowHeroNoArticles && (
				<>
        <div className={cx(`layout-columns d-flex section-0`, styles.layoutColumns)}>
          <div className={cx(`layout-column column-0`)}>
            <ArticleTileBlock
              cssClasses="block-articles"
              blockConfig={{ showTitleLinks: false }} // Show title links for this block
              columns={
                [
                  {
                    width: '100%',
                    columnConfig: {
                      showColumnNav: false,
                      showTileMeta: false,
                      stackTiles: {
                        stackOnMobile: true, // Stack on mobile
                        stackOnTablet: true, // Stack on tablet
                      },
                    },
                    tileDirection: 'row',
                    articles: allArticles.slice(0, 6)
                      // .filter(a => a.image && a.image !== '')
                      // .slice(0, 1)
                      .map(article => {
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 150 }),
                            title: truncateStringByWord(article.title, 80),
                          },
                          options: {
                            textPosition: 'right',
                            isCoverImage: false, // Assuming these are cover images  
                            tileWidth: '33.33%',
                          },
                          cssClasses: 'block-article-col-1-tile',
                        };
                      }) || [],
                    cssClasses: `flex-grow-1 block-article-col block-article-col-1`,
                  },
                ] as ArticleTileColumn[]
              }
            />

            {!hideAdsFromBlock && (
              <div className={cx(`ad`, styles.ad)}>
                <div id={`gpt-ad-21875548389-collection_recirculation_native1-${blockIndex}-section-0`}
                  style={{ maxWidth: '100%', width: '90%', margin: '0 auto', minHeight: '50px' }}
                />
              </div>
            )}

            <ArticleTileBlock
              cssClasses="block-articles"
              blockConfig={{ showTitleLinks: false }} // Show title links for this block
              columns={
                [
                  {
                    width: '100%',
                    columnConfig: {
                      showColumnNav: false,
                      showTileMeta: false,
                      stackTiles: {
                        stackOnMobile: true, // Stack on mobile
                        stackOnTablet: true, // Stack on tablet
                      },
                    },
                    tileDirection: 'row',
                    articles: allArticles.slice(6, 12)
                      // .filter(a => a.image && a.image !== '')
                      // .slice(0, 1)
                      .map(article => {
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 150 }),
                            title: truncateStringByWord(article.title, 80),
                          },
                          options: {
                            textPosition: 'right',
                            tileWidth: '33.33%',
                          },
                          cssClasses: 'block-article-col-1-tile',
                        };
                      }) || [],
                    cssClasses: `flex-grow-1 block-article-col block-article-col-1`,
                  },
                ] as ArticleTileColumn[]
              }
            />            
          </div>
          <div className={cx(`articleBlock-sidebar`, styles.articleBlockSidebar)}>
            {/* {(sidebarReelArticles && sidebarReelArticles.length > 0) && (
              <>
                {sidebarReelArticlesLoading === true ? (
                  <SkeletonLoader loaderType={'TileBlock'} />
                ) : (
                  <ArticleReelBlock
                    articles={sidebarReelArticles}
                    styles={styles}
                  />
                )}
              </>
            )} */}
            <div className={cx(`sidebar-ad-container d-flex flex-column`, styles.articleBlockSidebarContainer)}>
              {!hideAdsFromBlock && (
                <div className={cx(`ad`, styles.ad)}>
                  <div id={`gpt-ad-21875548389-collection_sas_sticky-${blockIndex}-section-0`} />
                </div>
              )}
            </div>
          </div>
        </div>

        {!hideAdsFromBlock && (
          <div className={cx(`ad`, styles.ad)} style={{ marginTop: '35px' }}>
            <div id={`gpt-ad-21875548389-collection_sas_top-${blockIndex}-section-0`}
              style={{ minWidth: '320px', minHeight: '50px' }}
            />
          </div>
        )}

        <div className={cx(`layout-columns d-flex section-1`, styles.layoutColumns)}>
          <div className={cx(`layout-column column-0`)}>
            <ArticleTileBlock
              cssClasses="block-articles"
              blockConfig={{ showTitleLinks: false }} // Show title links for this block
              columns={
                [
                  {
                    width: '100%',
                    columnConfig: {
                      showColumnNav: false,
                      showTileMeta: false,
                      stackTiles: {
                        stackOnMobile: true, // Stack on mobile
                        stackOnTablet: true, // Stack on tablet
                      },
                    },
                    tileDirection: 'row',
                    articles: allArticles.slice(12, 18)
                      // .filter(a => a.image && a.image !== '')
                      // .slice(0, 1)
                      .map(article => {
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 150 }),
                            title: truncateStringByWord(article.title, 80),
                          },
                          options: {
                            textPosition: 'right',
                            //isCoverImage: false, // Assuming these are cover images  
                            tileWidth: '33.33%',
                          },
                          cssClasses: 'block-article-col-1-tile',
                        };
                      }) || [],
                    cssClasses: `flex-grow-1 block-article-col block-article-col-1`,
                  },
                ] as ArticleTileColumn[]
              }
            />

            <div className={cx(`ad`, styles.ad)}>
              <div id={`gpt-ad-21875548389-collection_recirculation_native1-${blockIndex}-section-1`}
                style={{ maxWidth: '100%', width: '90%', margin: '0 auto', minHeight: '50px' }}
              />
            </div>

            <ArticleTileBlock
              cssClasses="block-articles"
              blockConfig={{ showTitleLinks: false }} // Show title links for this block
              columns={
                [
                  {
                    width: '100%',
                    columnConfig: {
                      showColumnNav: false,
                      showTileMeta: false,
                      stackTiles: {
                        stackOnMobile: true, // Stack on mobile
                        stackOnTablet: true, // Stack on tablet
                      },
                    },
                    tileDirection: 'row',
                    articles: allArticles.slice(18, 24)
                      // .filter(a => a.image && a.image !== '')
                      // .slice(0, 1)
                      .map(article => {
                        return {
                          article: {
                            ...article,
                            image: cfImage(article.image, { width: 150 }),
                            title: truncateStringByWord(article.title, 80),
                          },
                          options: {
                            textPosition: 'right',
                            tileWidth: '33.33%',
                          },
                          cssClasses: 'block-article-col-1-tile',
                        };
                      }) || [],
                    cssClasses: `flex-grow-1 block-article-col block-article-col-1`,
                  },
                ] as ArticleTileColumn[]
              }
            />            
          </div>
          <div className={cx(`articleBlock-sidebar`, styles.articleBlockSidebar)}>
            <div className={cx(`sidebar-ad-container d-flex flex-column`, styles.articleBlockSidebarContainer)}>
              <div className={cx(`ad`, styles.ad)}>
                <div id={`gpt-ad-21875548389-collection_sas_sticky-${blockIndex}-section-1`} />
              </div>
            </div>
          </div>
        </div>

        {!hideAdsFromBlock && (
          <div className={cx(`ad`, styles.ad)} style={{ marginTop: '35px' }}>
            <div id={`gpt-ad-21875548389-collection_sas_bottom-${blockIndex}-section-0`}
              style={{ maxWidth: '100%', minHeight: '50px' }}
            />
          </div>
        )}
				</>
			)}
    </Spacer>
  );
};

export default compose(styled(blockStyles))(ArticleBlock);
